import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Col, Row, InputNumber, message, Switch } from 'antd';
import api from '../api/axios';
import { useProfile } from '../context/ProfileContext';
import { Country, State, City } from 'country-state-city';
import ISO6391 from 'iso-639-1';

// Import JSON data files
import religionData from '../data/religion.json';
import denominationData from '../data/denomination.json';
import casteData from '../data/caste.json';
import subcasteData from '../data/subcaste.json';

const { Option } = Select;

// Utility to generate height options from 4'0" to 7'0"
const generateHeightOptions = (minFeet = 4, maxFeet = 7) => {
	const options = [];
	for (let feet = minFeet; feet <= maxFeet; feet++) {
		for (let inch = 0; inch < 12; inch++) {
			// Don't go past maxFeet
			if (feet === maxFeet && inch > 0) break;
			options.push(`${feet}'${inch}"`);
		}
	}
	return options;
};
const heightOptions = generateHeightOptions();

const PartnerPreferences = () => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [selectedCountry, setSelectedCountry] = useState({ name: '', isoCode: '' });
	const [selectedState, setSelectedState] = useState({ name: '', isoCode: '' });
	const [formValuesLoaded, setFormValuesLoaded] = useState(false);
	const [currentFormValues, setCurrentFormValues] = useState({});

	// Strict preference toggles
	const [strictPreferences, setStrictPreferences] = useState({
		age: true,
		height: true,
		weight: true,
		religion: true,
		denomination: false,
		caste: true,
		sub_caste: false,
		education: false,
		occupation: false,
		employed_in: false,
		salary_range: false,
		languages_known: false,
		complexion: false,
		physical_status: false,
		country: false,
		state: false,
		city: false,
		looking_for: false
	});

	const { profileId } = useProfile();

	useEffect(() => {
		const fetchPartnerPreferences = async () => {
			try {
				const response = await api.get('/expectations/list', {
					params: { profile_id: profileId },
				});

				if (response.data && response.data.data) {
					const data = response.data.data;

					// Transform API response to form values
					const formValues = {};
					const strictPrefs = {
						age: true,
						height: true,
						weight: true,
						religion: true,
						denomination: false,
						caste: true,
						sub_caste: false,
						education: false,
						occupation: false,
						employed_in: false,
						salary_range: false,
						languages_known: false,
						complexion: false,
						physical_status: false,
						country: false,
						state: false,
						city: false,
						looking_for: false
					};

					// Handle range fields
					if (data.age_range_start !== undefined) formValues.age_range_start = data.age_range_start;
					if (data.age_range_end !== undefined) formValues.age_range_end = data.age_range_end;
					if (data.age_strict !== undefined) strictPrefs.age = data.age_strict;

					if (data.height_range_start !== undefined) formValues.height_range_start = data.height_range_start;
					if (data.height_range_end !== undefined) formValues.height_range_end = data.height_range_end;
					if (data.height_strict !== undefined) strictPrefs.height = data.height_strict;

					if (data.weight_range_start !== undefined) formValues.weight_range_start = data.weight_range_start;
					if (data.weight_range_end !== undefined) formValues.weight_range_end = data.weight_range_end;
					if (data.weight_strict !== undefined) strictPrefs.weight = data.weight_strict;

					// Handle array fields
					const arrayFields = ['religion', 'denomination', 'caste', 'sub_caste', 'education', 'occupation', 'employed_in', 'salary_range', 'languages_known', 'complexion', 'physical_status', 'country', 'state', 'city'];
					arrayFields.forEach(field => {
						if (data[field] !== undefined) {
							formValues[field] = data[field];
						}
						if (data[`${field}_strict`] !== undefined) {
							strictPrefs[field] = data[`${field}_strict`];
						}
					});

					// Handle looking_for
					if (data.looking_for !== undefined) {
						formValues.looking_for = data.looking_for;
					}

					// Set form values with a small delay to ensure form is ready
					setTimeout(() => {
						form.setFieldsValue(formValues);
						setCurrentFormValues(formValues);
						setFormValuesLoaded(true);
					}, 100);

					// Set strict preferences
					setStrictPreferences(strictPrefs);

					// Set initial country, state, city if available
					if (data.country && data.country.length > 0) {
						const countryName = data.country[0];
						const countryObj = Country.getAllCountries().find(c => c.name === countryName);
						setSelectedCountry(countryObj ? { name: countryObj.name, isoCode: countryObj.isoCode } : { name: '', isoCode: '' });

						if (data.state && data.state.length > 0) {
							const stateName = data.state[0];
							const stateObj = countryObj ? State.getStatesOfCountry(countryObj.isoCode).find(s => s.name === stateName) : null;
							setSelectedState(stateObj ? { name: stateObj.name, isoCode: stateObj.isoCode } : { name: '', isoCode: '' });
						} else {
							setSelectedState({ name: '', isoCode: '' });
						}
					} else {
						setSelectedCountry({ name: '', isoCode: '' });
						setSelectedState({ name: '', isoCode: '' });
					}
				}
			} catch (error) {
				console.error('Error fetching partner preferences:', error);
				message.error('Failed to load partner preferences.');
			}
		};

		fetchPartnerPreferences();
	}, [profileId, form]);

	const handleStrictToggle = (preference, checked) => {
		setStrictPreferences(prev => ({
			...prev,
			[preference]: checked
		}));
	};

	const onFinish = async (values) => {
		// Ensure country, state, city are arrays and numeric fields are numbers
		const payload = {
			age_range_start: values.age_range_start,
			age_range_end: values.age_range_end,
			age_strict: strictPreferences.age,
			height_range_start: values.height_range_start,
			height_range_end: values.height_range_end,
			height_strict: strictPreferences.height,
			weight_range_start: values.weight_range_start,
			weight_range_end: values.weight_range_end,
			weight_strict: strictPreferences.weight,
			religion: Array.isArray(values.religion) ? values.religion : [],
			religion_strict: strictPreferences.religion,
			denomination: Array.isArray(values.denomination) ? values.denomination : [],
			denomination_strict: strictPreferences.denomination,
			caste: Array.isArray(values.caste) ? values.caste : [],
			caste_strict: strictPreferences.caste,
			sub_caste: Array.isArray(values.sub_caste) ? values.sub_caste : [],
			sub_caste_strict: strictPreferences.sub_caste,
			education: Array.isArray(values.education) ? values.education : [],
			education_strict: strictPreferences.education,
			occupation: Array.isArray(values.occupation) ? values.occupation : [],
			occupation_strict: strictPreferences.occupation,
			employed_in: Array.isArray(values.employed_in) ? values.employed_in : [],
			employed_in_strict: strictPreferences.employed_in,
			salary_range: Array.isArray(values.salary_range) ? values.salary_range : [],
			salary_range_strict: strictPreferences.salary_range,
			languages_known: Array.isArray(values.languages_known) ? values.languages_known : [],
			languages_known_strict: strictPreferences.languages_known,
			complexion: Array.isArray(values.complexion) ? values.complexion : [],
			complexion_strict: strictPreferences.complexion,
			physical_status: Array.isArray(values.physical_status) ? values.physical_status : [],
			physical_status_strict: strictPreferences.physical_status,
			country: Array.isArray(values.country) ? values.country : [],
			country_strict: strictPreferences.country,
			state: Array.isArray(values.state) ? values.state : [],
			state_strict: strictPreferences.state,
			city: Array.isArray(values.city) ? values.city : [],
			city_strict: strictPreferences.city,
			looking_for: values.looking_for || ''
		};

		setLoading(true);
		try {
			const response = await api.post('/expectations/update', payload, {
				params: { profile_id: profileId },
			});

			if (response.status === 200) {
				message.success('Partner preferences updated successfully');
			}
		} catch (error) {
			console.error('Error updating partner preferences:', error);
			if (error.response && error.response.data) {
				console.error('API Error Response:', error.response.data);
			}
			message.error('Failed to update partner preferences.');
		} finally {
			setLoading(false);
		}
	};

	const onFinishFailed = (errorInfo) => {
		// Form validation failed
	};

	return (
		<Form
			key={formValuesLoaded ? 'loaded' : 'loading'}
			form={form}
			layout="vertical"
			onFinish={onFinish}
			onFinishFailed={onFinishFailed}
		>
			<Row gutter={16}>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Age Range">
						<div>
							<Row gutter={8}>
								<Col span={12}>
									<Form.Item name="age_range_start" noStyle>
										<InputNumber min={18} max={100} placeholder="Min Age" style={{ width: '100%' }} />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item name="age_range_end" noStyle>
										<InputNumber min={18} max={100} placeholder="Max Age" style={{ width: '100%' }} />
									</Form.Item>
								</Col>
							</Row>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.age}
									onChange={(checked) => handleStrictToggle('age', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.age ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Height Range">
						<div>
							<Row gutter={8}>
								<Col span={12}>
									<Form.Item name="height_range_start" noStyle>
										<Select placeholder="Min Height">
											{heightOptions.map(h => (
												<Option key={h} value={h}>{h}</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item name="height_range_end" noStyle>
										<Select placeholder="Max Height">
											{heightOptions.map(h => (
												<Option key={h} value={h}>{h}</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
							</Row>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.height}
									onChange={(checked) => handleStrictToggle('height', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.height ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Weight Range (kg)">
						<div>
							<Row gutter={8}>
								<Col span={12}>
									<Form.Item name="weight_range_start" noStyle>
										<InputNumber min={30} max={200} placeholder="Min Weight" style={{ width: '100%' }} />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item name="weight_range_end" noStyle>
										<InputNumber min={30} max={200} placeholder="Max Weight" style={{ width: '100%' }} />
									</Form.Item>
								</Col>
							</Row>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.weight}
									onChange={(checked) => handleStrictToggle('weight', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.weight ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Religion" name="religion">
						<div>
							<Select
								mode="multiple"
								showSearch
								placeholder="Select Religion"
								optionFilterProp="children"
								value={currentFormValues.religion || []}
								onChange={value => {
									form.setFieldsValue({ religion: value });
									setCurrentFormValues(prev => ({
										...prev,
										religion: value || []
									}));
								}}
							>
								{religionData.religions.map(religion => (
									<Option key={religion.id} value={religion.name}>
										{religion.name}
									</Option>
								))}
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.religion}
									onChange={(checked) => handleStrictToggle('religion', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.religion ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Denomination" name="denomination">
						<div>
							<Select
								mode="multiple"
								showSearch
								placeholder="Select Denomination"
								optionFilterProp="children"
								value={currentFormValues.denomination || []}
								onChange={value => {
									form.setFieldsValue({ denomination: value });
									setCurrentFormValues(prev => ({
										...prev,
										denomination: value || []
									}));
								}}
							>
								{denominationData.denominations.map(denomination => (
									<Option key={denomination.id} value={denomination.name}>
										{denomination.name}
									</Option>
								))}
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.denomination}
									onChange={(checked) => handleStrictToggle('denomination', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.denomination ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Caste" name="caste">
						<div>
							<Select
								mode="multiple"
								showSearch
								placeholder="Select Caste"
								optionFilterProp="children"
								value={currentFormValues.caste || []}
								onChange={value => {
									form.setFieldsValue({ caste: value });
									setCurrentFormValues(prev => ({
										...prev,
										caste: value || []
									}));
								}}
							>
								{casteData.castes.map(caste => (
									<Option key={caste.id} value={caste.name}>
										{caste.name}
									</Option>
								))}
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.caste}
									onChange={(checked) => handleStrictToggle('caste', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.caste ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Sub Caste" name="sub_caste">
						<div>
							<Select
								mode="multiple"
								showSearch
								placeholder="Select Sub Caste"
								optionFilterProp="children"
								value={currentFormValues.sub_caste || []}
								onChange={value => {
									form.setFieldsValue({ sub_caste: value });
									setCurrentFormValues(prev => ({
										...prev,
										sub_caste: value || []
									}));
								}}
							>
								{subcasteData.subcastes.map(subcaste => (
									<Option key={subcaste.id} value={subcaste.name}>
										{subcaste.name}
									</Option>
								))}
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.sub_caste}
									onChange={(checked) => handleStrictToggle('sub_caste', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.sub_caste ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Education" name="education">
						<div>
							<Select
								mode="multiple"
								placeholder="Select Education"
								value={currentFormValues.education || []}
								onChange={value => {
									form.setFieldsValue({ education: value });
									setCurrentFormValues(prev => ({
										...prev,
										education: value || []
									}));
								}}
							>
								<Option value="Below 10th">Below 10th</Option>
								<Option value="10th Pass / SSLC">10th Pass / SSLC</Option>
								<Option value="12th Pass / HSC">12th Pass / HSC</Option>
								<Option value="Diploma / Polytechnic">Diploma / Polytechnic</Option>
								<Option value="Graduate / Bachelor's">Graduate / Bachelor's</Option>
								<Option value="Postgraduate / Master's">Postgraduate / Master's</Option>
								<Option value="Doctorate / PhD">Doctorate / PhD</Option>
								<Option value="Professional Degree">Professional Degree</Option>
								<Option value="Other">Other</Option>
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.education}
									onChange={(checked) => handleStrictToggle('education', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.education ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Occupation" name="occupation">
						<div>
							<Select
								mode="multiple"
								placeholder="Select Occupation"
								value={currentFormValues.occupation || []}
								onChange={value => {
									form.setFieldsValue({ occupation: value });
									setCurrentFormValues(prev => ({
										...prev,
										occupation: value || []
									}));
								}}
							>
								<Option value="Engineer">Engineer</Option>
								<Option value="Doctor">Doctor</Option>
								<Option value="Teacher">Teacher</Option>
								<Option value="Business">Business</Option>
								<Option value="Self-employed">Self-employed</Option>
								<Option value="Lawyer">Lawyer</Option>
								<Option value="Architect">Architect</Option>
								<Option value="Scientist">Scientist</Option>
								<Option value="Artist">Artist</Option>
								<Option value="Researcher">Researcher</Option>
								<Option value="Writer">Writer</Option>
								<Option value="CA/Accountant">CA/Accountant</Option>
								<Option value="IT Professional">IT Professional</Option>
								<Option value="Other">Other</Option>
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.occupation}
									onChange={(checked) => handleStrictToggle('occupation', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.occupation ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Employed In" name="employed_in">
						<div>
							<Select
								mode="multiple"
								placeholder="Select Employment Status"
								value={currentFormValues.employed_in || []}
								onChange={value => {
									form.setFieldsValue({ employed_in: value });
									setCurrentFormValues(prev => ({
										...prev,
										employed_in: value || []
									}));
								}}
							>
								<Option value="Government">Government</Option>
								<Option value="Private">Private</Option>
								<Option value="Business">Business</Option>
								<Option value="Self-employed">Self-employed</Option>
								<Option value="Defence/Civil Services">Defence/Civil Services</Option>
								<Option value="NGO/Social Sector">NGO/Social Sector</Option>
								<Option value="Student">Student</Option>
								<Option value="Homemaker">Homemaker</Option>
								<Option value="Retired">Retired</Option>
								<Option value="Not working">Not working</Option>
								<Option value="Other">Other</Option>
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.employed_in}
									onChange={(checked) => handleStrictToggle('employed_in', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.employed_in ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Salary Range" name="salary_range">
						<div>
							<Select
								mode="multiple"
								placeholder="Select Salary Range"
								value={currentFormValues.salary_range || []}
								onChange={value => {
									form.setFieldsValue({ salary_range: value });
									setCurrentFormValues(prev => ({
										...prev,
										salary_range: value || []
									}));
								}}
							>
								<Option value="0-5 Lakhs">0-5 Lakhs</Option>
								<Option value="5-10 Lakhs">5-10 Lakhs</Option>
								<Option value="10-15 Lakhs">10-15 Lakhs</Option>
								<Option value="15-20 Lakhs">15-20 Lakhs</Option>
								<Option value="20-25 Lakhs">20-25 Lakhs</Option>
								<Option value="25-30 Lakhs">25-30 Lakhs</Option>
								<Option value="30-40 Lakhs">30-40 Lakhs</Option>
								<Option value="40-50 Lakhs">40-50 Lakhs</Option>
								<Option value="50 Lakhs - 1 Crore">50 Lakhs - 1 Crore</Option>
								<Option value="1-2 Crore">1-2 Crore</Option>
								<Option value="2-5 Crore">2-5 Crore</Option>
								<Option value="5-10 Crore">5-10 Crore</Option>
								<Option value="10-20 Crore">10-20 Crore</Option>
								<Option value="20-50 Crore">20-50 Crore</Option>
								<Option value="50-100 Crore">50-100 Crore</Option>
								<Option value="100+ Crore">100+ Crore</Option>
								<Option value="No Income">No Income</Option>
								<Option value="Prefer not to say">Prefer not to say</Option>
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.salary_range}
									onChange={(checked) => handleStrictToggle('salary_range', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.salary_range ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Languages Known" name="languages_known">
						<div>
							<Select
								mode="multiple"
								showSearch
								placeholder="Select Language(s)"
								value={currentFormValues.languages_known || []}
								onChange={value => {
									form.setFieldsValue({ languages_known: value });
									setCurrentFormValues(prev => ({
										...prev,
										languages_known: value || []
									}));
								}}
							>
								{ISO6391.getAllCodes().map(code => (
									<Option key={code} value={ISO6391.getName(code)}>
										{ISO6391.getName(code)}
									</Option>
								))}
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.languages_known}
									onChange={(checked) => handleStrictToggle('languages_known', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.languages_known ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Complexion" name="complexion">
						<div>
							<Select
								mode="multiple"
								placeholder="Select Complexion"
								value={currentFormValues.complexion || []}
								onChange={value => {
									form.setFieldsValue({ complexion: value });
									setCurrentFormValues(prev => ({
										...prev,
										complexion: value || []
									}));
								}}
							>
								<Option value="Fair">Fair</Option>
								<Option value="Medium">Medium</Option>
								<Option value="Dark">Dark</Option>
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.complexion}
									onChange={(checked) => handleStrictToggle('complexion', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.complexion ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Physical Status" name="physical_status">
						<div>
							<Select
								mode="multiple"
								placeholder="Select Physical Status"
								value={currentFormValues.physical_status || []}
								onChange={value => {
									form.setFieldsValue({ physical_status: value });
									setCurrentFormValues(prev => ({
										...prev,
										physical_status: value || []
									}));
								}}
							>
								<Option value="Normal">Normal</Option>
								<Option value="Physically Challenged">Physically Challenged</Option>
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.physical_status}
									onChange={(checked) => handleStrictToggle('physical_status', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.physical_status ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Country" name="country">
						<div>
							<Select
								mode="multiple"
								showSearch
								placeholder="Select Country"
								value={currentFormValues.country || []}
								onChange={value => {
									// Update form values
									form.setFieldsValue({ country: value });
									
									// Update current form values state
									setCurrentFormValues(prev => ({
										...prev,
										country: value || []
									}));
									
									// Update selected country for state/city cascading
									if (value && value.length > 0) {
										const countryName = value[0];
										const countryObj = Country.getAllCountries().find(c => c.name === countryName);
										setSelectedCountry(countryObj ? { name: countryObj.name, isoCode: countryObj.isoCode } : { name: '', isoCode: '' });
										
										// Clear state and city when country changes
										setSelectedState({ name: '', isoCode: '' });
										form.setFieldsValue({ state: undefined, city: undefined });
										setCurrentFormValues(prev => ({
											...prev,
											state: undefined,
											city: undefined
										}));
									} else {
										setSelectedCountry({ name: '', isoCode: '' });
										setSelectedState({ name: '', isoCode: '' });
									}
								}}
								optionFilterProp="children"
							>
								{Country.getAllCountries().map(country => (
									<Option key={country.isoCode} value={country.name}>
										{country.name}
									</Option>
								))}
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.country}
									onChange={(checked) => handleStrictToggle('country', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.country ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="State" name="state">
						<div>
							<Select
								mode="multiple"
								showSearch
								placeholder="Select State"
								value={currentFormValues.state || []}
								onChange={value => {
									// Update form values
									form.setFieldsValue({ state: value });
									
									// Update current form values state
									setCurrentFormValues(prev => ({
										...prev,
										state: value || []
									}));
									
									// Update selected state for city cascading
									if (value && value.length > 0) {
										const stateName = value[0];
										const stateObj = selectedCountry.isoCode ? State.getStatesOfCountry(selectedCountry.isoCode).find(s => s.name === stateName) : null;
										setSelectedState(stateObj ? { name: stateObj.name, isoCode: stateObj.isoCode } : { name: '', isoCode: '' });
										
										// Clear city when state changes
										form.setFieldsValue({ city: undefined });
										setCurrentFormValues(prev => ({
											...prev,
											city: undefined
										}));
									} else {
										setSelectedState({ name: '', isoCode: '' });
									}
								}}
								disabled={!selectedCountry.isoCode}
								optionFilterProp="children"
							>
								{selectedCountry.isoCode &&
									State.getStatesOfCountry(selectedCountry.isoCode).map(state => (
										<Option key={state.isoCode} value={state.name}>
											{state.name}
										</Option>
									))}
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.state}
									onChange={(checked) => handleStrictToggle('state', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.state ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="City" name="city">
						<div>
							<Select
								mode="multiple"
								showSearch
								placeholder="Select City"
								value={currentFormValues.city || []}
								onChange={value => {
									// Update form values
									form.setFieldsValue({ city: value });
									
									// Update current form values state
									setCurrentFormValues(prev => ({
										...prev,
										city: value || []
									}));
								}}
								disabled={!selectedState.isoCode}
								optionFilterProp="children"
							>
								{selectedCountry.isoCode && selectedState.isoCode &&
									City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map(city => (
										<Option key={city.name} value={city.name}>
											{city.name}
										</Option>
									))}
							</Select>
							<div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									size="small"
									checked={strictPreferences.city}
									onChange={(checked) => handleStrictToggle('city', checked)}
								/>
								<span style={{ fontSize: '12px', color: '#666' }}>
									{strictPreferences.city ? 'Strict' : 'Preferred'}
								</span>
							</div>
						</div>
					</Form.Item>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Form.Item label="Looking For" name="looking_for">
						<Input.TextArea placeholder="Description of what they are looking for" rows={1} />
					</Form.Item>
				</Col>
			</Row>
			<Form.Item>
				<Button
					type="primary"
					htmlType="submit"
					style={{ backgroundColor: 'green', borderColor: 'green' }}
					loading={loading}
				>
					Submit
				</Button>
			</Form.Item>
		</Form>
	);
};

export default PartnerPreferences;